"use client";

import { useTransition } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import posthog from "posthog-js";

import { cn } from "@/lib/utils";

export interface DoseOption {
	variantId: string;
	label: string; // e.g. "10mg" / "5ml"
	priceLabel: string; // formatted price string
	packBasisLabel?: string;
	available: boolean;
	packCount?: number;
}

interface DosePickerProps {
	options: DoseOption[];
	selectedVariantId: string | undefined;
	productSlug: string;
	channel: string;
	selectionDimension: "dose" | "generic";
	ariaLabel?: string;
	className?: string;
}

const COLS_BY_COUNT: Record<number, string> = {
	1: "grid-cols-1",
	2: "grid-cols-2",
	3: "grid-cols-3",
	4: "grid-cols-4",
	5: "grid-cols-5",
};

export function DosePicker({
	options,
	selectedVariantId,
	productSlug,
	channel,
	selectionDimension,
	ariaLabel = "Dosierung",
	className,
}: DosePickerProps) {
	const router = useRouter();
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const [isPending, startTransition] = useTransition();

	const select = (option: DoseOption) => {
		if (!option.available || !option.variantId || option.variantId === selectedVariantId) return;
		posthog.capture("variant_selected", {
			product_slug: productSlug,
			channel,
			previous_variant_id: selectedVariantId,
			variant_id: option.variantId,
			selection_dimension: selectionDimension,
			dose: option.label,
			pack_count: option.packCount,
			available: true,
		});
		const params = new URLSearchParams(searchParams);
		params.set("variant", option.variantId);
		startTransition(() => {
			router.replace(`${pathname}?${params.toString()}`, { scroll: false });
		});
	};

	const colsClass = COLS_BY_COUNT[options.length] ?? "grid-cols-5";

	return (
		<div role="radiogroup" aria-label={ariaLabel} className={cn("grid gap-2", colsClass, className)}>
			{options.map((opt) => {
				const active = opt.variantId === selectedVariantId;
				return (
					<button
						key={opt.variantId}
						type="button"
						role="radio"
						aria-checked={active}
						aria-disabled={!opt.available || isPending}
						disabled={!opt.available || isPending}
						onClick={() => select(opt)}
						className={cn(
							"flex flex-col items-center gap-1 rounded-xl border-2 p-3 transition-colors",
							active
								? "border-primary bg-primary text-primary-foreground"
								: "hover:border-primary/40 border-border bg-card text-foreground",
							!opt.available && "cursor-not-allowed line-through opacity-50",
						)}
					>
						<span className="text-base font-bold">{opt.label}</span>
						<span className={cn("text-[10px]", active ? "opacity-80" : "opacity-70")}>{opt.priceLabel}</span>
						{opt.packBasisLabel && <span className="text-xs">{opt.packBasisLabel}</span>}
					</button>
				);
			})}
		</div>
	);
}
